import { parseBooleanSetting } from "../../common/utils";

const RE_WRAPUP_GROUP = /^\/reqWrapup\/(reqWrapupGroup\d+)$/;
const RE_WRAPUP_GROUP_VAL = /^\/reqWrapup\/(reqWrapupGroup\d+)Val(\d+)$/;
const RE_WRAPUP_GROUP_DEFAULT = /^\/reqWrapup\/(reqWrapupGroup\d+)DefaultValue$/;

const cleanValues = (str) => {
  return str
    .split(";")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const sortWrapupValueKeys = (keys) => {
  return [...keys].sort();
};

const getWrapupGroupIndex = (groupKey) => {
  const groupNum = parseInt(String(groupKey || "").replace(/\D/g, ""), 10);
  if (Number.isNaN(groupNum) || groupNum <= 0) {
    return -1;
  }
  return groupNum - 1;
};

const createEmptyWrapupGroup = () => ({ name: "", values: [], defaultValue: "" });

const createEmptySettings = () => ({
  wrapupGroups: [],
  recordTypeIdForCallTask: "",
  showEditSubjectField: false,
  translate911: false,
  translated911Number: "",
  translated933Number: "",
  enableOutboundScreenPopForClickToDial: false,
  enableOutboundScreenPopForDialPad: false,
  outboundScreenPopFlow: "",
  deferInboundScreenPop: false,
  loginMessage: {
    line1: "",
    line2: "",
  },
  softphoneLabel: "",
  sfObjectsForPhoneBookSearch: [],
});

let settings = createEmptySettings();

export const isOutboundScreenPopForClickToDialEnabled = () => {
  return settings.enableOutboundScreenPopForClickToDial;
};

export const isOutboundScreenPopForDialPadEnabled = () => {
  return settings.enableOutboundScreenPopForDialPad;
};
export const isDeferInboundScreenPop = () => {
  return settings.deferInboundScreenPop;
};

export const getOutboundScreenPopFlow = () => {
  return settings.outboundScreenPopFlow;
};

export const getLoginMessage = () => {
  return settings.loginMessage;
};

export const getSoftphoneLabel = () => {
  return settings.softphoneLabel;
};

const ALLOWED_SF_PHONEBOOK_OBJECTS = new Set(["account", "contact", "lead", "user"]);

export const getSfObjectsForPhoneBookSearch = () => {
  return settings.sfObjectsForPhoneBookSearch.filter((obj) =>
    ALLOWED_SF_PHONEBOOK_OBJECTS.has(obj.toLowerCase()),
  );
};

export const getRecordTypeIdForCallTask = () => {
  return settings.recordTypeIdForCallTask;
};

export const isShowEditSubjectFieldEnabled = () => {
  return settings.showEditSubjectField;
};

export const isTranslate911Enabled = () => {
  return settings.translate911;
};

export const getTranslated911Number = () => {
  return settings.translated911Number;
};

export const getTranslated933Number = () => {
  return settings.translated933Number;
};

export const getWrapupConfiguration = () => {
  return settings.wrapupGroups;
};

export const parseCallCenterSettings = (jsonSettings) => {
  settings = createEmptySettings();
  const ensureWrapupGroup = (groupKey) => {
    const groupIndex = getWrapupGroupIndex(groupKey);
    if (groupIndex < 0) {
      return createEmptyWrapupGroup();
    }
    if (!settings.wrapupGroups[groupIndex]) {
      settings.wrapupGroups[groupIndex] = createEmptyWrapupGroup();
    }
    return settings.wrapupGroups[groupIndex];
  };

  const orderedKeys = sortWrapupValueKeys(Object.keys(jsonSettings || {}));
  for (const key of orderedKeys) {
    const val = jsonSettings[key];

    const groupMatch = key.match(RE_WRAPUP_GROUP);
    if (groupMatch) {
      const groupKey = groupMatch[1];
      ensureWrapupGroup(groupKey).name = val ? String(val).trim() : "";
      continue;
    }

    const valueMatch = key.match(RE_WRAPUP_GROUP_VAL);
    if (valueMatch) {
      const groupKey = valueMatch[1];
      const group = ensureWrapupGroup(groupKey);
      if (val) {
        group.values.push(...cleanValues(String(val)));
      } else if (!group.values || group.values.length === 0) {
        const groupIndex = getWrapupGroupIndex(groupKey);
        if (groupIndex >= 0) {
          delete settings.wrapupGroups[groupIndex];
        }
      }
      continue;
    }

    const groupDefaultMatch = key.match(RE_WRAPUP_GROUP_DEFAULT);
    if (groupDefaultMatch) {
      const groupKey = groupDefaultMatch[1];
      ensureWrapupGroup(groupKey).defaultValue = val || "";
      continue;
    }

    if (key === "/reqLoginMessage/reqLoginMessageLine1") {
      settings.loginMessage.line1 = val || "";
      continue;
    }

    if (key === "/reqLoginMessage/reqLoginMessageLine2") {
      settings.loginMessage.line2 = val || "";
      continue;
    }

    if (key === "/reqGeneralInfo/reqSoftphoneLabel") {
      settings.softphoneLabel = val || "";
      continue;
    }

    if (key === "/reqCallSettings/reqSfObjectForPhoneBookSearch") {
      settings.sfObjectsForPhoneBookSearch = val ? cleanValues(String(val)) : [];
      continue;
    }

    if (key.endsWith("reqUseRecordTypeForCallTask")) {
      settings.recordTypeIdForCallTask = val;
      continue;
    }

    if (key === "/reqCallSettings/reqShowEditSubjectField") {
      settings.showEditSubjectField = parseBooleanSetting(val, settings.showEditSubjectField);
      continue;
    }

    if (key === "/reqLoginMessage/reqTranslate911") {
      settings.translate911 = parseBooleanSetting(val, settings.translate911);
      continue;
    }

    if (key === "/reqLoginMessage/req911TranslatedNumber") {
      settings.translated911Number = val || "";
      continue;
    }

    if (key === "/reqLoginMessage/req933TranslatedNumber") {
      settings.translated933Number = val || "";
      continue;
    }

    if (key == "/reqOutboundScreenPopSettings/reqEnableScreenPopOnClickToDial") {
      settings.enableOutboundScreenPopForClickToDial = parseBooleanSetting(
        val,
        settings.enableOutboundScreenPopForClickToDial,
      );
      continue;
    }

    if (key == "/reqOutboundScreenPopSettings/reqEnableScreenPopOnDialPad") {
      settings.enableOutboundScreenPopForDialPad = parseBooleanSetting(
        val,
        settings.enableOutboundScreenPopForDialPad,
      );
      continue;
    }

    if (key == "/reqOutboundScreenPopSettings/reqScreenPopFlow") {
      settings.outboundScreenPopFlow = val;
      continue;
    }

    if (key == "/reqInboundScreenPopSettings/reqDeferPop") {
      settings.deferInboundScreenPop = parseBooleanSetting(val, settings.deferInboundScreenPop);
      continue;
    }
  }
};
